import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateQuoteDialog } from "@/app/Quotes/Components/CreateQuoteDialog";
import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  Navbar,
  NavbarItemLink,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { quoteStatusCountQuery } from "@/app/Quotes/GraphQL/quoteStatusCountQuery";
import { QuoteStatus } from "@/gql/graphql";
import { QuoteTable } from "@/app/Quotes/Components/QuoteTable";
import { Suspense } from "react";
import { Button } from "@/components/catalyst/button";
import { useTranslations } from "use-intl";

export function QuoteTableScreen() {
  const t = useTranslations("quote");
  const { open } = useDialog(CreateQuoteDialog);

  return (
    <FullPageScreen>
      <Header />
      <QuoteTable
        id="quotes"
        buttons={
          <Button
            onClick={() => {
              open({});
            }}
          >
            <PlusIcon />
            {t("addQuote")}
          </Button>
        }
      />
    </FullPageScreen>
  );
}

type QuoteFilter = {
  labelKey: keyof typeof import("@/app/Quotes/messages/quoteMessages").quoteMessages;
  value: QuoteStatus[] | undefined;
  badgeColor?: BadgeColor;
};

const quoteFilters: QuoteFilter[] = [
  {
    labelKey: "all",
    value: [],
    badgeColor: "zinc",
  },
  {
    labelKey: "accepted",
    value: [QuoteStatus.Accepted],
    badgeColor: "green",
  },
  {
    labelKey: "issued",
    value: [QuoteStatus.Issued],
    badgeColor: "yellow",
  },
  {
    labelKey: "draft",
    value: [QuoteStatus.Draft],
  },
  {
    labelKey: "invalid",
    value: [QuoteStatus.Expired, QuoteStatus.Rejected, QuoteStatus.Voided],
  },

  {
    labelKey: "invoiced",
    value: [QuoteStatus.Invoiced],
  },
];

function Header() {
  "use no memo";

  const t = useTranslations("quote");
  const { data } = useSuspenseGqlQuery(quoteStatusCountQuery, {});
  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/quotes/",
  });
  const matchRoute = useMatchRoute();

  return (
    <Suspense>
      <div className="relative border-b border-gray-200 sm:pb-0">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {t("quotes")}
        </h3>
        <div className="sm:hidden">
          <label htmlFor="current-tab" className="sr-only">
            {t("selectTab")}
          </label>
          <select
            id="current-tab"
            name="current-tab"
            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
            onChange={(e) => {
              const statuses = quoteFilters.find(
                (tab) => t(tab.labelKey) === e.target.value,
              );

              navigate({
                to: "/$tenant/$orgId/supplier/{-$supplierId}/quotes",
                params: (prev) => prev,
                search: {
                  quoteStatus: statuses?.value ?? [],
                },
              });
            }}
          >
            {quoteFilters.map((tab, idx) => (
              <option key={idx} value={t(tab.labelKey)}>
                {t(tab.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <Navbar className="-mb-px">
          <NavbarSection className="max-lg:hidden">
            {quoteFilters.map(({ labelKey, value, badgeColor }, idx) => {
              const countItem = data.quoteStatusCount.find((quoteCountItem) => {
                return value?.includes(quoteCountItem.status);
              });
              return (
                <NavbarItemLink
                  key={idx}
                  to="/$tenant/$orgId/supplier/{-$supplierId}/quotes"
                  params={(prev) => prev}
                  current={
                    !!matchRoute({
                      to: "/$tenant/$orgId/supplier/{-$supplierId}/quotes",
                      search: {
                        quoteStatus: value,
                      },
                    })
                  }
                  search={{
                    quoteStatus: value,
                  }}
                >
                  {t(labelKey)}

                  {countItem && badgeColor ? (
                    <div>
                      <Badge className="py-0" color={badgeColor}>
                        {countItem.count}
                      </Badge>
                    </div>
                  ) : null}
                </NavbarItemLink>
              );
            })}
          </NavbarSection>
        </Navbar>
      </div>
    </Suspense>
  );
}
